<template>
  <section
    class="bg-white py-space-md py-lg-space-xxl px-space-md px-lg-space-xxxl"
  >
    <div class="container">
      <div class="row">
        <div class="col col-lg-10 offset-lg-1">
          <!-- title -->
          <h2 class="h2-medium mb-space-sm" id="degree-detail">{{ title }}</h2>
          <!-- description -->
          <div v-html="getDescription"></div>

          <!-- read more link -->
          <div class="text-center" v-if="showReadMore">
            <button
              class="btn btn-link text-decoration-underline show-text text-primary fs-medium p-0"
              @click="toggleReadMore"
            >
              Read more
            </button>
          </div>

          <!-- additional content -->
          <div v-show="showMoreText">
            <div v-html="getMoreDescription"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";

interface Props {
  title: string;
  description: string;
}

const props = defineProps<Props>();
const parsedDescription = ref<string[]>([]);
const splitIdx = ref<number>(5);
const showMoreText = ref(false);

const setDescription = () => {
  const { description } = props;
  // init DOMParser
  const parser = new DOMParser();
  if (description) {
    // format description to by get rid of linebreak/tab/carriage-return
    const formattedDesc = description.replace(/(\r\n|\r\n\t|\n|\r|\t)/gm, "");
    // parse description text into DOM Parser
    const doc = parser.parseFromString(formattedDesc, "text/html");
    const result: string[] = [];
    const rootChildren = doc.body.children;
    for (let i = 0; i < rootChildren.length; i++) {
      const el = rootChildren[i];
      // only consider top-level p, ul, ol, div, h2 or h3
      if (["P", "UL", "OL", "DIV", "H2", "H3"].includes(el.tagName)) {
        result.push(el.outerHTML);
        // check if 5th item is header and readjust splitIdx to 4 in that case
        if (i === 4 && ["H2", "H3"].includes(el.tagName)) {
          splitIdx.value = 4;
        }
      }
    }
    // set parsed result to ref
    parsedDescription.value = result;
  }
};

const getDescription = computed(() => {
  if (parsedDescription.value.length === 0) return "";
  return parsedDescription.value.slice(0, splitIdx.value).join("");
});

const showReadMore = computed(() => {
  return (
    parsedDescription.value.slice(0, parsedDescription.value.length - 1)
      .length >= splitIdx.value && !showMoreText.value
  );
});

const getMoreDescription = computed(() => {
  return parsedDescription.value.slice(splitIdx.value).join("");
});

const toggleReadMore = () => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    props.title.toLowerCase(),
    "read more",
    "details"
  );
  showMoreText.value = !showMoreText.value;
};
onMounted(() => {
  setDescription();
});
</script>
